/**
 * Client proof points — case studies and testimonials — tagged by service slug
 * and industry so each service detail page can show only relevant results.
 */

export type CaseStudy = {
  id: string;
  industry: string;
  client: string;
  headline: string;
  challenge: string;
  approach: string;
  metrics: { value: string; label: string }[];
  services: string[];
};

export type Testimonial = {
  id: string;
  quote: string;
  author: string;
  role: string;
  industry: string;
  services: string[];
};

export const caseStudies: CaseStudy[] = [
  {
    id: "saas-close",
    industry: "Technology & SaaS",
    client: "Series A SaaS platform, 48 employees",
    headline: "Close cut from 21 days to 5 ahead of a funding round",
    challenge:
      "Deferred revenue was tracked in spreadsheets, and monthly statements landed three weeks late — too slow for board and investor reporting.",
    approach:
      "We rebuilt the revenue recognition schedule in the ledger, standardized the close checklist, and moved reconciliations to a weekly cadence.",
    metrics: [
      { value: "21 → 5 days", label: "Month-end close" },
      { value: "100%", label: "Deferred revenue reconciled" },
      { value: "$0", label: "Audit adjustments at year end" },
    ],
    services: [
      "month-end-close",
      "accounting",
      "financial-reporting",
      "bookkeeping",
      "reconciliations",
      "cfo-controller-support",
    ],
  },
  {
    id: "ecom-recs",
    industry: "eCommerce",
    client: "Multi-channel DTC brand, $14M revenue",
    headline: "Six sales channels reconciled to the penny every week",
    challenge:
      "Shopify, Amazon, and wholesale payouts never tied to the bank, leaving COGS and margin unreliable for buying decisions.",
    approach:
      "Channel-level clearing accounts, automated payout matching, and an inventory-to-COGS routine reviewed weekly.",
    metrics: [
      { value: "6", label: "Channels reconciled weekly" },
      { value: "99.8%", label: "Match rate on payouts" },
      { value: "4.2 pts", label: "Margin variance eliminated" },
    ],
    services: [
      "reconciliations",
      "bookkeeping",
      "accounting",
      "quickbooks-xero",
      "month-end-close",
      "financial-reporting",
    ],
  },
  {
    id: "agency-ar",
    industry: "Marketing Agencies",
    client: "Performance agency, 90 active retainers",
    headline: "DSO down 26 days without a single awkward client call",
    challenge:
      "Invoices went out late and collections were ad hoc, so cash was consistently tight in the first half of each month.",
    approach:
      "Fixed billing calendar, automated dunning sequences by aging bucket, and a weekly collections review with the account leads.",
    metrics: [
      { value: "58 → 32 days", label: "Days sales outstanding" },
      { value: "$610K", label: "Cash pulled forward in 90 days" },
      { value: "3 days", label: "Invoice issue lag, from 12" },
    ],
    services: [
      "accounts-receivable",
      "bookkeeping",
      "finance-process-outsourcing",
      "quickbooks-xero",
    ],
  },
  {
    id: "construction-jobcost",
    industry: "Construction",
    client: "Commercial contractor, 40 concurrent jobs",
    headline: "WIP schedules that finally matched the field",
    challenge:
      "Job costing lived in the PM tool and never reconciled to the ledger, so WIP and retainage were guesses at close.",
    approach:
      "Mapped cost codes to the chart of accounts, rebuilt the WIP schedule, and added a monthly percent-complete review.",
    metrics: [
      { value: "40", label: "Jobs tracked to cost code" },
      { value: "±1.5%", label: "WIP accuracy vs. field estimate" },
      { value: "$280K", label: "Unbilled retainage recovered" },
    ],
    services: [
      "accounting",
      "month-end-close",
      "financial-reporting",
      "bookkeeping",
      "cfo-controller-support",
      "catch-up-cleanup",
    ],
  },
  {
    id: "healthcare-payroll",
    industry: "Healthcare",
    client: "Multi-site clinic group, 210 staff",
    headline: "Payroll across four states with zero filing penalties",
    challenge:
      "Shift differentials and multi-state withholding were handled manually, producing recurring corrections and two late filings.",
    approach:
      "Standardized pay codes, built a pre-run audit checklist, and took ownership of the filing calendar across all jurisdictions.",
    metrics: [
      { value: "210", label: "Employees paid on cycle" },
      { value: "0", label: "Late or penalized filings in 24 months" },
      { value: "9 hrs", label: "Admin time saved per pay run" },
    ],
    services: ["payroll", "finance-process-outsourcing", "bookkeeping", "accounting"],
  },
  {
    id: "manufacturing-ap",
    industry: "Manufacturing",
    client: "Contract manufacturer, 300+ vendors",
    headline: "Three-way match applied to every invoice, on time",
    challenge:
      "Invoice approvals sat in email, duplicate payments slipped through, and early-pay discounts were routinely missed.",
    approach:
      "Bill.com approval routing tied to PO and receipt data, plus a weekly payment run with a documented exception queue.",
    metrics: [
      { value: "1,400/mo", label: "Invoices processed" },
      { value: "$46K", label: "Annual early-pay discounts captured" },
      { value: "0", label: "Duplicate payments since go-live" },
    ],
    services: [
      "accounts-payable",
      "finance-process-outsourcing",
      "bookkeeping",
      "reconciliations",
      "accounting",
    ],
  },
  {
    id: "startup-cleanup",
    industry: "Startups",
    client: "Seed-stage fintech, 18 months of backlog",
    headline: "Two years of books cleaned up in seven weeks",
    challenge:
      "Founders had run the books themselves; diligence surfaced miscategorized spend, missing receipts, and unreconciled accounts.",
    approach:
      "Rebuilt the chart of accounts, reconciled every month back to inception, and produced an investor-ready statement pack.",
    metrics: [
      { value: "24 months", label: "Restated and reconciled" },
      { value: "7 weeks", label: "Start to clean close" },
      { value: "1,900+", label: "Transactions recategorized" },
    ],
    services: [
      "catch-up-cleanup",
      "bookkeeping",
      "accounting",
      "quickbooks-xero",
      "reconciliations",
      "month-end-close",
    ],
  },
  {
    id: "profservices-cfo",
    industry: "Professional Services",
    client: "Consulting firm, 65 consultants",
    headline: "Utilization and project margin visible before month-end",
    challenge:
      "Leadership only learned a project was underwater after it closed, and there was no rolling cash forecast.",
    approach:
      "Fractional controller support, a 13-week cash model, and project-level margin reporting refreshed every Monday.",
    metrics: [
      { value: "13 weeks", label: "Rolling cash visibility" },
      { value: "+6.8 pts", label: "Blended project margin" },
      { value: "Weekly", label: "Utilization reporting cadence" },
    ],
    services: ["cfo-controller-support", "financial-reporting", "accounting", "month-end-close"],
  },
  {
    id: "realestate-reporting",
    industry: "Real Estate",
    client: "Owner-operator, 22 properties",
    headline: "Property-level P&Ls and investor packs on a fixed date",
    challenge:
      "Investor reporting was assembled by hand each quarter and never arrived on the same schedule twice.",
    approach:
      "Class-based property tracking, a standard reporting template, and a published close calendar for the whole portfolio.",
    metrics: [
      { value: "22", label: "Property-level P&Ls monthly" },
      { value: "Day 6", label: "Investor pack delivered, every month" },
      { value: "11 hrs", label: "Manual reporting hours removed" },
    ],
    services: [
      "financial-reporting",
      "accounting",
      "bookkeeping",
      "month-end-close",
      "cfo-controller-support",
    ],
  },
  {
    id: "retail-qbo",
    industry: "Retail",
    client: "Nine-location specialty retailer",
    headline: "QuickBooks rebuilt for multi-location reporting",
    challenge:
      "A single flat company file made it impossible to see which locations were profitable, and POS never tied to deposits.",
    approach:
      "Restructured classes and locations, automated POS-to-deposit matching, and trained the in-house team on the new file.",
    metrics: [
      { value: "9", label: "Locations reported separately" },
      { value: "2 days", label: "POS reconciliation lag, from 3 weeks" },
      { value: "100%", label: "Deposits matched to sales" },
    ],
    services: [
      "quickbooks-xero",
      "reconciliations",
      "bookkeeping",
      "financial-reporting",
      "catch-up-cleanup",
    ],
  },
  {
    id: "nonprofit-fpo",
    industry: "Nonprofits",
    client: "Grant-funded nonprofit, $9M budget",
    headline: "Full back office outsourced, restricted funds tracked cleanly",
    challenge:
      "One overloaded staff accountant handled AP, payroll, and grant reporting — restricted balances were frequently misstated.",
    approach:
      "Took over the transactional finance function end to end, with fund-level tracking and a monthly grant compliance pack.",
    metrics: [
      { value: "17", label: "Grants tracked to restriction" },
      { value: "$118K", label: "Annual back-office cost saved" },
      { value: "Clean", label: "Two consecutive audit opinions" },
    ],
    services: [
      "finance-process-outsourcing",
      "accounts-payable",
      "payroll",
      "accounting",
      "financial-reporting",
      "month-end-close",
    ],
  },
  {
    id: "smb-ar-ap",
    industry: "Small & Medium Businesses",
    client: "Regional distributor, $22M revenue",
    headline: "Working capital freed on both sides of the ledger",
    challenge:
      "Receivables aged quietly while payables were paid the day they arrived, squeezing cash in a seasonal business.",
    approach:
      "Aligned collections and payment runs to a single weekly cash cycle with clear terms enforced on both sides.",
    metrics: [
      { value: "$840K", label: "Working capital released" },
      { value: "41 → 29 days", label: "Average collection period" },
      { value: "Weekly", label: "Cash position reporting" },
    ],
    services: [
      "accounts-receivable",
      "accounts-payable",
      "bookkeeping",
      "finance-process-outsourcing",
      "reconciliations",
    ],
  },
];

export const testimonials: Testimonial[] = [
  {
    id: "t-saas",
    quote:
      "We went from chasing our own numbers to reviewing them. The close lands on the same day every month, and our board deck writes itself now.",
    author: "VP of Finance",
    role: "Series A B2B SaaS Platform",
    industry: "Technology & SaaS",
    services: [
      "month-end-close",
      "financial-reporting",
      "accounting",
      "cfo-controller-support",
      "bookkeeping",
    ],
  },
  {
    id: "t-ecom",
    quote:
      "Every channel ties out weekly. For the first time I trust our margin by SKU enough to make buying decisions off it.",
    author: "Founder & CEO",
    role: "Direct-to-Consumer Apparel Brand",
    industry: "eCommerce",
    services: [
      "reconciliations",
      "bookkeeping",
      "quickbooks-xero",
      "accounting",
      "financial-reporting",
    ],
  },
  {
    id: "t-agency",
    quote:
      "Collections used to be my job on Friday nights. Their team handles the whole aging cycle and our cash position improved within a quarter.",
    author: "Managing Partner",
    role: "Growth Marketing Agency (25 Team Members)",
    industry: "Marketing Agencies",
    services: ["accounts-receivable", "finance-process-outsourcing", "bookkeeping"],
  },
  {
    id: "t-health",
    quote:
      "Multi-state payroll stopped being a source of anxiety. Two years in, not one correction or late filing.",
    author: "Operations Director",
    role: "Multi-Location Specialty Practice Group",
    industry: "Healthcare",
    services: ["payroll", "finance-process-outsourcing", "accounting"],
  },
  {
    id: "t-mfg",
    quote:
      "Invoices are matched, approved, and paid on a schedule I can plan around. We're finally capturing early-pay discounts.",
    author: "Corporate Controller",
    role: "Precision Contract Manufacturer",
    industry: "Manufacturing",
    services: ["accounts-payable", "finance-process-outsourcing", "reconciliations", "accounting"],
  },
  {
    id: "t-startup",
    quote:
      "They untangled two years of founder bookkeeping before our raise. Diligence came back with no accounting questions at all.",
    author: "Co-Founder",
    role: "Seed-Stage Fintech (Mercury & Stripe Stack)",
    industry: "Startups",
    services: [
      "catch-up-cleanup",
      "bookkeeping",
      "quickbooks-xero",
      "accounting",
      "month-end-close",
    ],
  },
  {
    id: "t-prof",
    quote:
      "Having a controller in the room one day a week changed how we price work. We see margin problems while we can still fix them.",
    author: "Managing Director",
    role: "Management Consulting Firm",
    industry: "Professional Services",
    services: ["cfo-controller-support", "financial-reporting", "month-end-close", "accounting"],
  },
  {
    id: "t-construction",
    quote:
      "Our WIP schedule finally reflects what's happening on site. Our surety and our bank both noticed the difference.",
    author: "Owner & Principal",
    role: "Commercial Electrical & HVAC Contractor",
    industry: "Construction",
    services: [
      "accounting",
      "financial-reporting",
      "month-end-close",
      "catch-up-cleanup",
      "bookkeeping",
    ],
  },
  {
    id: "t-realestate",
    quote:
      "Twenty-two property P&Ls, delivered on the sixth of every month. My investors stopped asking where the reports were.",
    author: "Principal Operator",
    role: "Multi-Unit Commercial Real Estate Portfolio",
    industry: "Real Estate",
    services: [
      "financial-reporting",
      "bookkeeping",
      "accounting",
      "month-end-close",
      "cfo-controller-support",
    ],
  },
  {
    id: "t-retail",
    quote:
      "They rebuilt our QuickBooks file properly and taught our team to keep it that way. Location-level reporting works now.",
    author: "Director of Finance",
    role: "Specialty Multi-Store Retail Brand",
    industry: "Retail",
    services: [
      "quickbooks-xero",
      "reconciliations",
      "catch-up-cleanup",
      "financial-reporting",
      "bookkeeping",
    ],
  },
  {
    id: "t-nonprofit",
    quote:
      "Restricted funds are tracked correctly and our audit was clean two years running. That was never true before.",
    author: "Executive Director",
    role: "Research & Education Foundation (501c3)",
    industry: "Nonprofits",
    services: [
      "finance-process-outsourcing",
      "accounts-payable",
      "payroll",
      "accounting",
      "financial-reporting",
    ],
  },
  {
    id: "t-smb",
    quote:
      "Receivables and payables now run on one weekly cycle. We stopped guessing about cash in our slow season.",
    author: "General Manager",
    role: "Regional Wholesale & Industrial Distributor",
    industry: "Small & Medium Businesses",
    services: [
      "accounts-receivable",
      "accounts-payable",
      "bookkeeping",
      "reconciliations",
      "finance-process-outsourcing",
    ],
  },
];

export function caseStudiesForService(slug: string) {
  return caseStudies.filter((c) => c.services.includes(slug));
}

export function testimonialsForService(slug: string) {
  return testimonials.filter((t) => t.services.includes(slug));
}
